import { LAMPORTS_PER_SOL, PublicKey } from '@solana/web3.js'
import Decimal from 'decimal.js'
import { u128ToDecimal } from '../utils/HedgeConstants'
import { LiquidationPoolEra } from './LiquidationPoolEra'
import { LiquidationPoolState } from './LiquidationPoolState'

export class LiquidationPosition {
  public publicKey: PublicKey
  public eraPublicKey: PublicKey
  public era: LiquidationPoolEra
  public liquidationPoolState: LiquidationPoolState

  public ownerAccount: PublicKey
  public deposit: Decimal
  public closedUsh: Decimal
  public timestampOpened: number
  public timestampClosed: number

  public productSnapshotEntry: Decimal
  public productSnapshotClosed: Decimal
  public sumSnapshotsEntry: [Decimal]
  public sumSnapshotsClosed: [Decimal]
  public hedgeRewardsSnapshot: Decimal
  public open: boolean

  constructor(poolPositionInfo: any, key: PublicKey, era: LiquidationPoolEra, poolState: LiquidationPoolState) {
    this.publicKey = key
    this.era = era
    this.liquidationPoolState = poolState
    this.eraPublicKey = poolPositionInfo.era
    this.ownerAccount = poolPositionInfo.ownerAccount
    this.deposit = new Decimal(poolPositionInfo.deposit.toString())
    this.closedUsh = new Decimal(poolPositionInfo.closedUsh.toString())
    this.timestampOpened = poolPositionInfo.timestampOpened.toNumber()
    this.timestampClosed = poolPositionInfo.timestampClosed.toNumber()

    this.productSnapshotEntry = u128ToDecimal(
      poolPositionInfo.productSnapshotEntry.toString()
    )
    this.productSnapshotClosed = u128ToDecimal(
      poolPositionInfo.productSnapshotClosed.toString()
    )
    this.sumSnapshotsEntry = poolPositionInfo.sumSnapshotsEntry.map(
      (sum: any) => {
        return u128ToDecimal(sum.toString())
      }
    )
    this.sumSnapshotsClosed = poolPositionInfo.sumSnapshotsClosed.map(
      (sum: any) => {
        return u128ToDecimal(sum.toString())
      }
    )
    this.hedgeRewardsSnapshot = u128ToDecimal(
      poolPositionInfo.hedgeRewardsSnapshotAccum.toString()
    )
    this.open = !!poolPositionInfo.state.open
  }
  
  // Returns the initial USH deposit amount
  public getInitialDeposit(): Decimal {
    return new Decimal(this.deposit)
  }

  // Returns the current USH amount left on the position
  public getUshAvailable(): Decimal {
    return this.era.product
      .div(this.productSnapshotEntry)
      .mul(new Decimal(this.deposit))
      .floor()
  }

  public getTokensAvailable(index: number): Decimal {
    return this.era.sum[index]
      .minus(this.sumSnapshotsEntry[index])
      .div(this.productSnapshotEntry)
      .mul(new Decimal(this.deposit))
      .floor()
  }
  
  public hasUnclaimedTokens(): boolean {
    for (let i = 0; i < this.era.sum.length; i++) {
      if (this.getTokensAvailable(i).gt(0)) {
        return true
      }
    }
    return false
  }

  public getHedgeAvailable (): Decimal {
    const LiquidationPoolTotalSupply = 2000000.0 * LAMPORTS_PER_SOL
    const hedgeRewardsSinceLastUpdate = hedgeRewardsDecay(
      LiquidationPoolTotalSupply,
      this.liquidationPoolState.hedgeInitRewardsTimestamp * 1000,
      this.era.hedgeRewardsTimestamp * 1000,
      Date.now(),
      365 * 1000)

    if (this.era.totalDeposits.isZero()) {
      return new Decimal(0)
    }

    const rewardsPerToken = this.era.product.mul(new Decimal(hedgeRewardsSinceLastUpdate).div(this.era.totalDeposits))
    const newAccumulator = rewardsPerToken.add(new Decimal(this.era.hedgeRewardsAccumulator))
    const hedgeAvailable = (newAccumulator.minus(this.hedgeRewardsSnapshot)).mul(new Decimal(this.deposit)).div(this.productSnapshotEntry)
    return hedgeAvailable
  }
}

function hedgeRewardsDecay (supply: number, birthTime: number, timeIn: number, timeOut: number, halfLifeInDays: number): number {
  const timeInOffsetStart = timeIn - birthTime
  const timeOutOffsetStart = timeOut - birthTime
  const halfLife = -1 * Math.log(2) / (halfLifeInDays * 60 * 60 * 24)
  const awardedTokens = supply * (Math.pow(Math.E, halfLife * timeInOffsetStart) - Math.pow(Math.E, halfLife * timeOutOffsetStart))
  return awardedTokens
}
